var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetModuleSubpanelsQuery } from "@/store/api";
import SubpanelDisplay from "./subpanelDisplay";
import { borderColorTabSelectedClass, borderRadiusTabSelectedClass, borderRadiusTabClass, borderRadiusTabHoverClass, spacingOngletsClass, } from "@/utils/funcs";
import { useAppSelector } from "@/hooks/store";
import { getUserPreferences } from "@/store/utils/selectors/userSelector";
var Subpanels = function (_a) {
    var moduleName = _a.moduleName, record = _a.record;
    var t = useTranslation(["common"]).t;
    var preferences = useAppSelector(getUserPreferences);
    var useTabView = preferences.use_tab_for_subpanel;
    // Load subpanels
    var subpanelsList = useGetModuleSubpanelsQuery(moduleName || "").data;
    var _b = useState(0), currentBatch = _b[0], setCurrentBatch = _b[1];
    var batchSize = 1;
    var _c = useState(0), activeTab = _c[0], setActiveTab = _c[1];
    // Memoize subpanels to prevent recalculations on every render
    var subpanels = useMemo(function () {
        if (subpanelsList === null || subpanelsList === void 0 ? void 0 : subpanelsList.data) {
            return Object.entries(subpanelsList.data.subpanels);
        }
        return [];
    }, [subpanelsList]);
    // Determine displayed subpanels based on the current batch
    var displayedSubpanels = useMemo(function () {
        return subpanels.slice(0, (currentBatch + 1) * batchSize);
    }, [subpanels, currentBatch]);
    useEffect(function () {
        var interval = setInterval(function () {
            if (displayedSubpanels.length < subpanels.length) {
                setCurrentBatch(function (prevBatch) { return prevBatch + 1; });
            }
            else {
                clearInterval(interval);
            }
        }, 500);
        return function () { return clearInterval(interval); };
    }, [displayedSubpanels.length, subpanels.length]);
    var getModuleStrings = useMemo(function () { return function (subpanel) {
        if (subpanel && subpanel.type !== "collection") {
            var strings = t(subpanel.module);
            return typeof strings === "string" ? {} : strings;
        }
        return {};
    }; }, [t]);
    // Get subpanel label
    var getSubpanelLabel = function (subpanelMeta) {
        if (subpanelMeta === null || subpanelMeta === void 0 ? void 0 : subpanelMeta.title_key) {
            return t(subpanelMeta.title_key, {
                ns: moduleName,
            });
        }
        if (subpanelMeta === null || subpanelMeta === void 0 ? void 0 : subpanelMeta.module) {
            var moduleStr = t(subpanelMeta.module);
            return typeof moduleStr === "object" && moduleStr && moduleStr
                ? moduleStr
                : subpanelMeta.module;
        }
        return "Sous-panneau";
    };
    // Rendu conditionnel basé sur la configuration
    if (useTabView) {
        // Vue par onglets
        return (_jsxs("div", __assign({ className: "flex flex-col ".concat(spacingOngletsClass) }, { children: [_jsx("ul", __assign({ className: "flex justify-start w-full flex-wrap text-sm font-medium text-center text-[var(--colors-tab)] gap-y-2 gap-x-0" }, { children: displayedSubpanels.map(function (_a, index) {
                        var subpanelId = _a[0], subpanelMeta = _a[1];
                        return (_jsx("li", __assign({ role: "presentation" }, { children: _jsx("button", __assign({ type: "button", role: "tab", "aria-selected": index === activeTab, "aria-controls": "subpanel-".concat(subpanelId), id: "tab-".concat(subpanelId), className: "relative block p-4 ".concat(spacingOngletsClass, " mb-1 ").concat(index === activeTab
                                    ? "text-[var(--colors-tab)] bg-[var(--colors-tab-selected)]\n                         ".concat(borderRadiusTabSelectedClass, "\n                         ").concat(borderColorTabSelectedClass)
                                    : "".concat(borderRadiusTabClass, " ").concat(borderRadiusTabHoverClass, "\n                         hover:text-[var(--colors-tab-text-survol)]\n                         text-[var(--colors-tab-text)]\n                         bg-[var(--colors-tab)]\n                         hover:bg-[var(--colors-tab-hovered)]")), onClick: function (e) {
                                    e.stopPropagation();
                                    setActiveTab(index);
                                }, onKeyDown: function (e) {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setActiveTab(index);
                                    }
                                }, tabIndex: 0, style: index === activeTab
                                    ? {
                                        borderTopWidth: "var(--border-width-tab-selected-top)",
                                        borderBottomWidth: "var(--border-width-tab-selected-bottom)",
                                        borderLeftWidth: "var(--border-width-tab-selected-left)",
                                        borderRightWidth: "var(--border-width-tab-selected-right)",
                                    }
                                    : undefined }, { children: getSubpanelLabel(subpanelMeta) })) }), subpanelId));
                    }) })), _jsx("div", __assign({ className: "mt-4" }, { children: displayedSubpanels.map(function (_a, index) {
                        var subpanelId = _a[0], subpanelMeta = _a[1];
                        return index === activeTab && (_jsx("div", { children: _jsx(SubpanelDisplay, { moduleName: moduleName, recordId: record === null || record === void 0 ? void 0 : record.id, subpanelId: subpanelId, subpanelMeta: subpanelMeta, moduleStrings: getModuleStrings(subpanelMeta), parentModuleStrings: t("app"), itemsPerPage: 4, useTab: useTabView, topButtons: subpanelMeta === null || subpanelMeta === void 0 ? void 0 : subpanelMeta.top_buttons }) }, subpanelId));
                    }) }))] })));
    }
    else {
        // Ancien affichage vertical
        return (_jsx("div", __assign({ className: "flex flex-col" }, { children: displayedSubpanels.map(function (_a, index) {
                var subpanelId = _a[0], subpanelMeta = _a[1];
                return (_jsx(SubpanelDisplay, { moduleName: moduleName, recordId: record === null || record === void 0 ? void 0 : record.id, subpanelId: subpanelId, subpanelMeta: subpanelMeta, moduleStrings: getModuleStrings(subpanelMeta), parentModuleStrings: t("app"), itemsPerPage: 4, topButtons: subpanelMeta === null || subpanelMeta === void 0 ? void 0 : subpanelMeta.top_buttons }, subpanelId));
            }) })));
    }
};
export default React.memo(Subpanels);
